import { useTranslation } from 'react-i18next';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { useMyTasksController } from '../hooks/useMyTasksController';
import { TasksPageHeader } from '../components/tasks/TasksPageHeader';
import { TasksFilters } from '../components/tasks/TasksFilters';
import { TasksTable } from '../components/tasks/TasksTable';
import { TaskTypeCounters } from '../components/tasks/TaskTypeCounters';
import { TaskValidationModal } from '../components/tasks/TaskValidationModal';
import { DiffuseDocumentModal } from '../components/documents/DiffuseDocumentModal';
import { Pagination } from '../components/ui/Pagination';

export const MyTasksPage = () => {
  const { t } = useTranslation();
  const {
    selectedCompanyId,
    currentUserRole,
    canViewAllTasks,
    activeTab,
    currentPage,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    userFilter,
    setUserFilter,
    userOptions,
    tasksData,
    isTasksLoading,
    tasksError,
    diffuseModalRef,
    validationModalRef,
    setCurrentPage,
    handleTabChange,
    handleResetFilters,
    handleRefresh,
    handleTaskAction,
    handleViewDocument,
    handleSendReminder,
    pendingReminderTaskId,
  } = useMyTasksController();

  if (!selectedCompanyId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <p className="text-gray-500">{t('common.error')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="flex-1 h-full flex flex-col max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-lg">
          <TasksPageHeader
            totalCount={tasksData?.total ?? 0}
            activeTab={activeTab}
            canViewAllTasks={canViewAllTasks}
            onTabChange={handleTabChange}
            onRefresh={handleRefresh}
          />

          {tasksData && <TaskTypeCounters counts={tasksData.pendingCountsByType} />}

          <TasksFilters
            search={search}
            onSearchChange={setSearch}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            showUserFilter={activeTab === 'all'}
            userOptions={userOptions}
            userFilter={userFilter}
            onUserFilterChange={setUserFilter}
            onReset={handleResetFilters}
          />

          {isTasksLoading && (
            <div className="flex justify-center items-center py-12">
              <CircleNotchIcon size={32} className="animate-spin text-primary" />
            </div>
          )}

          {tasksError && <div className="p-6 text-center text-red-600">{t('common.error')}</div>}

          {!isTasksLoading && !tasksError && (
            <div className="px-4 pb-6 sm:px-6">
              <TasksTable
                tasks={tasksData?.tasks ?? []}
                scope={activeTab}
                pendingReminderTaskId={pendingReminderTaskId}
                onAction={handleTaskAction}
                onViewDocument={handleViewDocument}
                onSendReminder={handleSendReminder}
              />

              {tasksData && tasksData.totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={tasksData.totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <DiffuseDocumentModal
        ref={diffuseModalRef}
        companyId={selectedCompanyId}
        currentUserRole={currentUserRole}
      />

      <TaskValidationModal ref={validationModalRef} companyId={selectedCompanyId} />
    </div>
  );
};
